import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import UniversalNavbar from './UniversalNavbar';
import VFooter from './VFooter';
import API_BASE_URL from '../config/api';
import { PrimaryButton } from '../atelier';

import { devLog } from '../utils/devLog';

function ResetPassword() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // 'checking' | 'valid' | 'invalid' | 'success'
  const [tokenState, setTokenState] = useState('checking');
  const [tokenError, setTokenError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const checkToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/reset_password/${token}`);
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.ok && data.valid) {
          setTokenState('valid');
        } else {
          setTokenState('invalid');
          setTokenError(data.error || 'Invalid or expired reset link.');
        }
      } catch (err) {
        if (cancelled) return;
        devLog.error('Reset token check failed:', err);
        setTokenState('invalid');
        setTokenError('Could not verify the reset link. Please check your connection and try again.');
      }
    };
    checkToken();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reset_password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, confirm_password: confirmPassword }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setTokenState('success');
      } else if (data.valid === false) {
        // Token got consumed/expired between page load and submit.
        setTokenState('invalid');
        setTokenError(data.error || 'Invalid or expired reset link.');
      } else {
        setError(data.error || 'Could not reset password. Please try again.');
      }
    } catch (err) {
      devLog.error('Reset password error:', err);
      setError('An unexpected error occurred. Please check your internet connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  const card = 'rounded-2xl border border-line bg-white p-6 sm:p-8 shadow-[0_20px_44px_-36px_rgba(22,19,15,0.4)]';
  const orangePillLink =
    'flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold text-white bg-primary transition-transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_12px_26px_-10px_rgba(212,86,20,0.5)]';

  const passwordField = (label, value, onChange, show, toggleShow, id, helpText) => (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-bold text-ink">{label}</label>
      <div className="flex items-center gap-3 rounded-xl border border-line bg-white px-4 h-[52px] transition-colors focus-within:border-primary">
        <Lock size={18} className="flex-shrink-0 text-muted" />
        <input
          type={show ? 'text' : 'password'}
          id={id}
          name={id}
          value={value}
          onChange={onChange}
          required
          minLength={8}
          className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-gray-400"
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={toggleShow}
          className="text-gray-400 transition-colors hover:text-gray-600"
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {helpText && <p className="mt-1.5 text-[12.5px] text-muted">{helpText}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-bone text-ink">
      <UniversalNavbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            {/* Header */}
            <motion.div variants={itemVariants} className="text-center mb-8">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary mb-3">
                Password recovery
              </p>
              <h1 className="font-fell text-4xl font-semibold leading-[1.05] text-ink mb-3">
                Set a new password.
              </h1>
              <p className="text-[15px] text-muted">
                Choose a strong password for your account.
              </p>
            </motion.div>

            {tokenState === 'checking' && (
              <motion.div variants={itemVariants}>
                <div className={`${card} text-center`}>
                  <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                  <p className="text-[14.5px] text-muted">Checking your reset link…</p>
                </div>
              </motion.div>
            )}

            {tokenState === 'invalid' && (
              <motion.div variants={itemVariants}>
                <div className={`${card} text-center`}>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="text-red-500" size={32} />
                  </div>
                  <h3 className="font-fell text-2xl font-semibold text-ink mb-3">This link isn't valid</h3>
                  <p className="text-[14.5px] text-muted mb-6">{tokenError}</p>
                  <Link to="/forgot-password" className={orangePillLink}>
                    Request a new reset link
                  </Link>
                  <div className="mt-4">
                    <Link
                      to="/login"
                      className="inline-flex items-center text-[13.5px] font-semibold text-primary transition-opacity hover:opacity-70"
                    >
                      <ArrowLeft size={16} className="mr-2" />
                      Back to login
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            {tokenState === 'success' && (
              <motion.div variants={itemVariants}>
                <div className={`${card} text-center`}>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="font-fell text-2xl font-semibold text-ink mb-3">Password reset</h3>
                  <p className="text-[14.5px] text-muted mb-6">
                    Your password has been changed successfully. Log in with your new password to continue.
                  </p>
                  <Link to="/login" className={orangePillLink}>
                    <ArrowLeft size={18} />
                    Go to login
                  </Link>
                </div>
              </motion.div>
            )}

            {tokenState === 'valid' && (
              <motion.div variants={itemVariants}>
                <div className={card}>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13.5px] font-semibold text-red-700"
                      role="alert"
                    >
                      {error}
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {passwordField(
                      'New password',
                      password,
                      (e) => setPassword(e.target.value),
                      showPassword,
                      () => setShowPassword((s) => !s),
                      'password',
                      'At least 8 characters. Avoid common or all-numeric passwords.'
                    )}
                    {passwordField(
                      'Confirm new password',
                      confirmPassword,
                      (e) => setConfirmPassword(e.target.value),
                      showConfirm,
                      () => setShowConfirm((s) => !s),
                      'confirm_password'
                    )}

                    <PrimaryButton type="submit" disabled={isLoading} className="w-full">
                      {isLoading ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                          Resetting password…
                        </>
                      ) : (
                        'Reset password'
                      )}
                    </PrimaryButton>
                  </form>

                  <div className="mt-6 text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center text-[13.5px] font-semibold text-primary transition-opacity hover:opacity-70"
                    >
                      <ArrowLeft size={16} className="mr-2" />
                      Back to login
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Help Text */}
            <motion.div variants={itemVariants} className="mt-8 text-center">
              <p className="text-[13px] text-muted mb-2">Need more help?</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center text-[13px]">
                <a
                  href="mailto:team@vasstr.com"
                  className="font-semibold text-primary transition-opacity hover:opacity-70"
                >
                  team@vasstr.com
                </a>
                <span className="hidden sm:inline text-gray-400">•</span>
                <span className="text-gray-600">+91 97043 87878</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <VFooter />
    </div>
  );
}

export default ResetPassword;
